/**
 * Weather: cloud cover, rain, wind, ground wetness, and how each of those changes the
 * light, the air and the grip.
 *
 * OWNER: piece:atmosphere
 *
 * The state lives in the exported `Weather` object so any other system can read it
 * without importing this module's internals or caring how it got there:
 *
 *   import { Weather } from '../world/weather';
 *   const mu = baseGrip * Weather.gripMultiplier;   // physics
 *   material.roughness = lerp(dry, wet, Weather.wetness);   // terrain shading
 *   dustEmission *= 1 - Weather.wetness;            // vfx
 *   rainBed.gain = Weather.rainRate;                // audio
 *
 * Presets are deliberately discrete (clear / overcast / rain) with a continuous blend
 * between them, because a game needs weather you can *name* — "it's raining, the roots
 * are lethal" — not a stochastic soup nobody can reason about. Nothing changes on its
 * own unless `Weather.autoCycle` is turned on, so captures and runs are reproducible.
 */

import * as THREE from 'three';
import { registerSystem, type SystemContext } from '../core/systems';
import { activeSky, skyParams } from '../render/sky';
import { clamp01, damp, lerp } from '../core/math';

export type WeatherKind = 'clear' | 'overcast' | 'rain';

interface WeatherPreset {
  cloudCover: number;
  turbidity: number;
  /** Fraction of direct sun that reaches the ground. */
  sunlight: number;
  cloudBrightness: number;
  /** Rain intensity, 0..1. */
  rainRate: number;
  /** Equilibrium ground water this weather drives toward. */
  wetTarget: number;
  mist: number;
  haze: number;
  windSpeed: number;
  ambientBoost: number;
}

const PRESETS: Record<WeatherKind, WeatherPreset> = {
  clear: {
    cloudCover: 0.32,
    turbidity: 2.0,
    sunlight: 1.0,
    cloudBrightness: 1.0,
    rainRate: 0,
    wetTarget: 0,
    mist: 1.0,
    haze: 1.0,
    windSpeed: 5.5,
    ambientBoost: 0,
  },
  overcast: {
    cloudCover: 0.94,
    turbidity: 4.2,
    sunlight: 0.14,
    cloudBrightness: 0.72,
    rainRate: 0,
    wetTarget: 0.18,
    mist: 1.9,
    haze: 1.45,
    windSpeed: 9.0,
    ambientBoost: 0.55,
  },
  rain: {
    cloudCover: 0.99,
    turbidity: 5.6,
    sunlight: 0.05,
    cloudBrightness: 0.5,
    rainRate: 1.0,
    wetTarget: 1.0,
    mist: 2.7,
    haze: 1.9,
    windSpeed: 13.0,
    ambientBoost: 0.7,
  },
};

export interface WeatherState {
  /** The preset being blended away from. */
  from: WeatherKind;
  /** The preset being blended toward — the "current weather" for UI purposes. */
  kind: WeatherKind;
  /** 0..1 progress from `from` to `kind`. */
  blend: number;

  /** 0 = clear sky, 1 = solid deck. */
  cloudCover: number;
  /** 0 = bone dry, 1 = running water. Terrain shading darkens and glosses with this. */
  wetness: number;
  /**
   * Multiply tire friction by this. Wet hardpack loses about a quarter of its grip and
   * wet roots far more than that; this is the global part, surface-specific loss is the
   * physics owner's to apply on top.
   */
  gripMultiplier: number;
  /** 0..1 rain intensity. */
  rainRate: number;
  /** Fraction of direct sunlight reaching the ground. */
  sunlight: number;
  /** m/s, and the horizontal direction it blows toward. */
  windSpeed: number;
  windX: number;
  windZ: number;
  /** Hours 0..24. Mirrors the sky's clock so other systems have one place to read it. */
  timeOfDay: number;
  /** When true the weather drifts by itself. Off by default: runs stay reproducible. */
  autoCycle: boolean;
  /** Hours of game time per real second when `autoCycle` is on. */
  dayRate: number;
}

export const Weather: WeatherState = {
  from: 'clear',
  kind: 'clear',
  blend: 1,
  cloudCover: PRESETS.clear.cloudCover,
  wetness: 0,
  gripMultiplier: 1,
  rainRate: 0,
  sunlight: 1,
  windSpeed: PRESETS.clear.windSpeed,
  windX: 0.93,
  windZ: 0.37,
  timeOfDay: skyParams.timeOfDay,
  autoCycle: false,
  dayRate: 0.02,
};

/** Grip loss at full wetness. Wet dirt is roughly a quarter down on dry. */
export const WET_GRIP_LOSS = 0.27;
/** Seconds for the ground to soak up / dry out. Drying is much slower than wetting. */
const WET_RATE = 0.055;
const DRY_RATE = 0.012;

let transitionSeconds = 10;
let transitionElapsed = 1e9;

/** Change the weather. `seconds` is the crossfade, not an instant snap. */
export function setWeather(kind: WeatherKind, seconds = 10): void {
  if (kind === Weather.kind && transitionElapsed >= transitionSeconds) return;
  Weather.from = currentKindSnapshot();
  Weather.kind = kind;
  transitionSeconds = Math.max(seconds, 0.001);
  transitionElapsed = 0;
  Weather.blend = 0;
}

/** Jump straight to a weather state with no crossfade. */
export function forceWeather(kind: WeatherKind): void {
  Weather.from = kind;
  Weather.kind = kind;
  Weather.blend = 1;
  transitionElapsed = 1e9;
  applyBlend(0);
  // Wetness has its own long time constant; a forced state sets it outright.
  Weather.wetness = PRESETS[kind].wetTarget;
}

export function setTimeOfDay(hours: number): void {
  Weather.timeOfDay = ((hours % 24) + 24) % 24;
  skyParams.timeOfDay = Weather.timeOfDay;
}

function currentKindSnapshot(): WeatherKind {
  return Weather.blend >= 1 ? Weather.kind : Weather.from;
}

function lerpPreset(a: WeatherPreset, b: WeatherPreset, t: number, out: WeatherPreset): void {
  out.cloudCover = lerp(a.cloudCover, b.cloudCover, t);
  out.turbidity = lerp(a.turbidity, b.turbidity, t);
  out.sunlight = lerp(a.sunlight, b.sunlight, t);
  out.cloudBrightness = lerp(a.cloudBrightness, b.cloudBrightness, t);
  out.rainRate = lerp(a.rainRate, b.rainRate, t);
  out.wetTarget = lerp(a.wetTarget, b.wetTarget, t);
  out.mist = lerp(a.mist, b.mist, t);
  out.haze = lerp(a.haze, b.haze, t);
  out.windSpeed = lerp(a.windSpeed, b.windSpeed, t);
  out.ambientBoost = lerp(a.ambientBoost, b.ambientBoost, t);
}

const blended: WeatherPreset = { ...PRESETS.clear };

function applyBlend(dt: number): void {
  transitionElapsed += dt;
  const t = clamp01(transitionElapsed / transitionSeconds);
  // Smoothstep so a weather change eases in rather than ramping linearly.
  Weather.blend = t * t * (3 - 2 * t);
  lerpPreset(PRESETS[Weather.from], PRESETS[Weather.kind], Weather.blend, blended);

  Weather.cloudCover = blended.cloudCover;
  Weather.rainRate = blended.rainRate;
  Weather.sunlight = blended.sunlight;
  Weather.windSpeed = blended.windSpeed;
}

// ---------------------------------------------------------------------------
// Rain. One draw call of camera-anchored streaks. Cheap, and the difference between
// "the light went grey" and "it is raining on me".
// ---------------------------------------------------------------------------

const RAIN_STREAKS = 4200;
const RAIN_BOX = 38;

const rainVert = /* glsl */ `
attribute vec3 aSeed;
attribute float aTail;

uniform vec3 uOrigin;
uniform vec3 uVel;
uniform float uTime;
uniform float uBox;
uniform float uLen;
uniform float uAmount;

varying float vAlpha;

void main() {
  vec3 p = mod( aSeed * uBox + uVel * uTime, vec3( uBox ) );
  vec3 world = uOrigin + p - vec3( uBox * 0.5 );
  vec3 dir = normalize( uVel );
  world -= dir * ( aTail * uLen * ( 0.55 + 0.45 * aSeed.y ) );

  vec4 mv = modelViewMatrix * vec4( world, 1.0 );
  float dist = length( mv.xyz );
  // Fade the near field so streaks do not stab the camera, and the far field so the
  // box boundary never announces itself.
  vAlpha = uAmount * smoothstep( 1.2, 4.0, dist ) * ( 1.0 - smoothstep( 12.0, 22.0, dist ) );
  gl_Position = projectionMatrix * mv;
}
`;

const rainFrag = /* glsl */ `
precision mediump float;
varying float vAlpha;
uniform vec3 uTint;
void main() {
  if ( vAlpha <= 0.002 ) discard;
  gl_FragColor = vec4( uTint, vAlpha );
  // ShaderMaterials do not get tone mapping or output encoding automatically; without
  // these the streaks sit in a different colour space from everything behind them.
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

class Rain {
  readonly object: THREE.LineSegments;
  private readonly uniforms: Record<string, THREE.IUniform>;
  private time = 0;

  constructor() {
    const geo = new THREE.BufferGeometry();
    const n = RAIN_STREAKS * 2;
    const pos = new Float32Array(n * 3);
    const seed = new Float32Array(n * 3);
    const tail = new Float32Array(n);
    let s = 1337;
    const rnd = () => {
      s = (s * 1664525 + 1013904223) >>> 0;
      return s / 4294967296;
    };
    for (let i = 0; i < RAIN_STREAKS; i++) {
      const x = rnd();
      const y = rnd();
      const z = rnd();
      for (let e = 0; e < 2; e++) {
        const j = i * 2 + e;
        seed[j * 3] = x;
        seed[j * 3 + 1] = y;
        seed[j * 3 + 2] = z;
        tail[j] = e;
      }
    }
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 3));
    geo.setAttribute('aTail', new THREE.BufferAttribute(tail, 1));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    this.uniforms = {
      uOrigin: { value: new THREE.Vector3() },
      uVel: { value: new THREE.Vector3(2.5, -19, 1) },
      uTime: { value: 0 },
      uBox: { value: RAIN_BOX },
      uLen: { value: 1.15 },
      uAmount: { value: 0 },
      uTint: { value: new THREE.Color(0.72, 0.78, 0.86) },
    };

    const mat = new THREE.ShaderMaterial({
      vertexShader: rainVert,
      fragmentShader: rainFrag,
      uniforms: this.uniforms,
      transparent: true,
      depthWrite: false,
      fog: false,
    });
    this.object = new THREE.LineSegments(geo, mat);
    this.object.frustumCulled = false;
    this.object.renderOrder = 950;
    this.object.visible = false;
  }

  update(dt: number, camera: THREE.Camera, rate: number, wind: number, wx: number, wz: number): void {
    const amount = clamp01(rate);
    this.object.visible = amount > 0.01;
    if (!this.object.visible) return;

    // Wrapped so float32 precision in the vertex shader stays tight over a long session.
    this.time = (this.time + dt) % 3600;
    const fall = -(13 + 9 * amount);
    (this.uniforms.uVel.value as THREE.Vector3).set(wx * wind * 0.55, fall, wz * wind * 0.55);
    this.uniforms.uTime.value = this.time;
    this.uniforms.uLen.value = 0.85 + 1.35 * amount;
    this.uniforms.uAmount.value = 0.55 * amount;

    // Anchor the field to the world in box-sized steps so streaks show parallax as you
    // ride instead of hanging off the camera like a decal.
    const c = camera.position;
    (this.uniforms.uOrigin.value as THREE.Vector3).set(
      Math.round(c.x / RAIN_BOX) * RAIN_BOX,
      Math.round(c.y / RAIN_BOX) * RAIN_BOX,
      Math.round(c.z / RAIN_BOX) * RAIN_BOX,
    );
  }

  setTint(r: number, g: number, b: number): void {
    (this.uniforms.uTint.value as THREE.Color).setRGB(r, g, b);
  }
}

// ---------------------------------------------------------------------------

let rain: Rain | null = null;
let gustPhase = 0;
/** Scratch for the camera bearing. Module scope: the update path allocates nothing. */
const viewFwd = new THREE.Vector3();

registerSystem({
  id: 'weather',
  order: 15,

  init(ctx: SystemContext) {
    // The sky is built in the composition root, which has no system context — this is
    // the first point at which anything the atmosphere owns can see the heightfield.
    // Without it the mountain cannot shadow itself.
    activeSky.current?.attachTerrain(ctx.terrain);

    rain = new Rain();
    ctx.scene.add(rain.object);
    applyBlend(0);
    Weather.wetness = PRESETS[Weather.kind].wetTarget;
    pushToSky();

    // Debug/authoring hook. The capture harness drives this to shoot the same vista
    // under different weather and light without needing a new scenario per state.
    (window as unknown as Record<string, unknown>).__gripWeather = {
      state: Weather,
      set: setWeather,
      force: forceWeather,
      time: setTimeOfDay,
      presets: Object.keys(PRESETS),
    };
  },

  fixedUpdate(dt: number) {
    // Wetness and grip belong to the sim clock: they are handling, not decoration.
    const target = blended.wetTarget;
    const rate = target > Weather.wetness ? WET_RATE : DRY_RATE;
    Weather.wetness = damp(Weather.wetness, target, rate, dt);
    Weather.gripMultiplier = 1 - WET_GRIP_LOSS * Weather.wetness;
  },

  update(dt: number, ctx: SystemContext) {
    applyBlend(dt);

    if (Weather.autoCycle) {
      setTimeOfDay(Weather.timeOfDay + Weather.dayRate * dt);
    }

    // Wind direction wanders slowly; gusts ride on top of the base speed.
    gustPhase += dt * 0.37;
    const gust = 1 + 0.35 * Math.sin(gustPhase) * Math.sin(gustPhase * 0.41 + 1.7);
    const wind = Weather.windSpeed * gust;
    const dirAngle = 0.38 + 0.25 * Math.sin(gustPhase * 0.19);
    Weather.windX = Math.cos(dirAngle);
    Weather.windZ = Math.sin(dirAngle);

    pushToSky();

    const sky = activeSky.current;
    if (sky) {
      sky.advance(dt);
      // The sun shadow cascade is small enough now that where it is pointed matters.
      // The camera's own bearing is the best available proxy for "the ground the player
      // is about to ride", and this system is the only part of the atmosphere that is
      // handed a camera.
      ctx.camera.getWorldDirection(viewFwd);
      sky.setViewBearing(viewFwd.x, viewFwd.z);
      // Read-only: the bike sim owns this state, we only need to know where to put the
      // rider's own contact shadow.
      const b = ctx.bike;
      sky.setRider(b.x, b.y, b.z, b.yaw);
      if (rain) {
        // Rain picks up the sky's own colour so it never reads as white confetti.
        const h = sky.horizonColor;
        rain.setTint(
          Math.min(h.r * 0.55 + 0.12, 1.4),
          Math.min(h.g * 0.55 + 0.14, 1.4),
          Math.min(h.b * 0.55 + 0.18, 1.4),
        );
      }
    }

    if (rain) rain.update(dt, ctx.camera, Weather.rainRate, wind, Weather.windX, Weather.windZ);
  },

  dispose() {
    if (rain) {
      rain.object.geometry.dispose();
      (rain.object.material as THREE.Material).dispose();
      rain = null;
    }
  },
});

function pushToSky(): void {
  skyParams.cloudCover = Weather.cloudCover;
  skyParams.turbidity = blended.turbidity;
  skyParams.cloudBrightness = blended.cloudBrightness;
  skyParams.sunDim = blended.sunlight;
  skyParams.mistAmount = blended.mist;
  skyParams.hazeAmount = blended.haze;
  skyParams.ambientBoost = blended.ambientBoost;
  skyParams.windX = Weather.windX * Weather.windSpeed;
  skyParams.windZ = Weather.windZ * Weather.windSpeed;
  Weather.timeOfDay = skyParams.timeOfDay;
}
